import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input } from 'antd'
import { EyeOutlined, SearchOutlined } from '@ant-design/icons'

import User from "../../models/User"

import ErrorBanner from '../../components/ErrorBanner'
import LoadingBanner from '../../components/LoadingBanner'
import InfoTooltip from "../../components/InfoTooltip"
import PageNotAllowed from '../../components/errors/PageNotAllowed';
import Component from '../../components/Component';
import LoadingSpinner from '../../components/LoadingSpinner';

import Page from "../Page";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";

export default class ContractsList extends Page {
	static path = '/contracts';

	constructor(props, context) {
		super(props, context);

		this.state = {
			...this.state,
			platformContract: undefined,
			contractedOpportunities: undefined,
			isLoading: true,
			error: null,
			isDisabled: false,
		};
	}

	componentDidMount() {
		super.componentDidMount();

		if (!this.context.auth?.isTalentSupplierResource()) {
			this.fetchContracts();
		}
	}

	async fetchContracts() {
		let user = this.context?.auth;
		if (!user) {
			return;
		}

		await this.promisedState({
			platformContract: undefined,
			contractedOpportunities: undefined,
			isLoading: true,
			error: null,
		});

		let contractGroups = await user.fetchContracts().catch(console.warn);

		if (!contractGroups) {
			return this.handleErrorResponse(
				User.FetchTalentContractsRequest, {
				500: 'error_show_talent_contracts',
			}
			);
		}

		let platformContract = contractGroups.platform,
			contractedOpportunities = contractGroups.opportunities;

		// console.log(contractedOpportunities)

		await this.promisedState({
			platformContract,
			contractedOpportunities,
			isLoading: false,
		});
	}

	async signMasterAgreement() {
		let user = this.context?.auth;
		if (!user) {
			return;
		}

		await this.promisedState({
			isDisabled: true,
		});

		let updatedUser = await user.signMasterContract().catch(console.warn);

		// console.log(updatedUser);

		if (!updatedUser) {
			await this.promisedState({
				isDisabled: false,
			});

			// handle error
			return;// TB.sysAlert('talent.contracts_sign_master_failed');
		}

		let contract = { ...this.state.platformContract };
		contract.accepted_at = updatedUser.agreement_signed_at;

		await this.promisedState({
			platformContract: contract,
		});

		// success message + link to finish profile
		await TB.sysAlert('talent.contracts_signed_master_agreement');

		await this.promisedState({
			isDisabled: false,
		});

		this.props.navigate.push(
			user.currentApplicationStep().getPath()
			// TalentDashboardPage.path
		);
	}

	renderMasterContractSection() {
		if (!this.state.platformContract) {
			return <div />;
		}

		let acceptedAt = this.state.platformContract.accepted_at;
		acceptedAt = acceptedAt ? TB.moment(acceptedAt) : null;
		if (acceptedAt && !acceptedAt.isValid()) {
			acceptedAt = null;
		}

		return (
			<div className='master-contract-container px-3 py-4'>
				<p className="mb-0"
					style={{ flex: 1 }}>
					{TB.trans('talent.contracts_list.master_contract_title')}
					`<InfoTooltip placement={'right'}>
						{TB.trans('talent.contracts_list.master_contract_tooltips')}
					</InfoTooltip>`
				</p>

				<div>

					{TB.isDefined(this.state.platformContract.path) && (
						<a href={this.state.platformContract.path}
							className="btn btn-outline-dark mr-3"
							target="_blank">
							{TB.trans('talent.contracts_list.view_pdf')}
						</a>
					)}

					{TB.isDefined(this.state.platformContract.path) && !TB.isDefined(acceptedAt) && (
						<button type="button"
							className="btn btn-outline-dark"
							onClick={this.signMasterAgreement.bind(this)}
							disabled={this.state.isDisabled}>
							{TB.trans('talent.contracts_list.sign')}
						</button>
					)}

					{!TB.isDefined(this.state.platformContract.path) && (
						<p className="mb-0"
							style={{ display: 'inline-block', fontSize: 12 }}>
							Coming soon...
						</p>
					)}

					{TB.isDefined(acceptedAt) && (
						<p className="mb-0"
							style={{ display: 'inline-block', fontSize: 12 }}>
							{TB.trans('talent.contracts_list.signed')} {acceptedAt.format('MM/D/Y')}
						</p>
					)}
				</div>
			</div>
		);
	}

	renderMobile = () => {
		return (
			<div>
				{this.state.contractedOpportunities.map((opp, i) => {
					return (
						<div className="bg-white my-6 border rounded border-gray-300 p-6">

							<div className="flex flex-row items-center gap-3 pb-2">
								<img
									alt={opp.project.client.name}
									src={opp.project?.client?.logo_path || TB.logoImagePath()}
									className="w-[70px]"
								/>

								<p className="text-xl">
									{opp.project.client.name}
								</p>
							</div>

							<div className="py-2">
								<p className="text-xs">Project</p>
								<p>{opp.project.name}</p>
							</div>

							<div className="py-2">
								<p className="text-xs">Document</p>
								<p>{opp.document.name || 'N/A'}</p>
							</div>

							<div className="flex gap-6 py-2">
								<div className="w-full">
									<p className="text-xs">Signed</p>
									<p>{opp.signed_at ? TB.moment(opp.signed_at).format('MM/DD/YY') : 'Not yet Signed'}</p>
								</div>

								<div className="w-full">
									<a href={opp.document.path} className="btn btn-outline-dark" target="_blank">
										View PDF
									</a>
								</div>
							</div>
						</div>
					)
				})}
			</div>
		)
	}

	renderProjectContractsSection() {
		if (!this.state.contractedOpportunities) {
			return <div />;
		}

		if (!this.state.contractedOpportunities.length) {
			return (
				<div className='empty-project-contracts-container mt-5 p-5'>
					{TB.trans('talent.contracts_list.no_projects_msg')}
				</div>
			);
		}

		return (
			<div className='project-contracts-container'>
				<table className="table table-bordered mt-4">
					<thead>
						<tr>
							<th style={{ width: '30%' }}>Client</th>
							<th style={{ width: '45%' }}>Project Name</th>
							<th style={{ width: '45%' }}>Document</th>
							<th style={{ width: '10%', minWidth: '120px' }}>Signed Date</th>
							<th style={{ width: '15%', minWidth: '155px' }}>Contract</th>
						</tr>
					</thead>
					<tbody>
						{this.state.contractedOpportunities.map((opp, i) => (
							<tr key={`talent-project-contracts-${i}`}>
								<td>
									<div className="flex flex-row items-center gap-3">
										<img
											alt={opp.project.client.name}
											src={opp.project?.client?.logo_path || TB.logoImagePath()}
											className="w-[50px]" />

										{opp.project.client.name}

									</div>
								</td>
								<td>
									{opp.project.name}
								</td>
								<td>
									{opp.document.name || 'N/A'}
								</td>
								<td className="text-center" >
									{opp.signed_at ? TB.moment(opp.signed_at).format('MM/DD/YY') : 'N/A'}
								</td>
								<td className="text-center">
									<a href={opp.document.path}
										className="btn btn-outline-dark"
										target="_blank">
										View PDF
									</a>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	render() {
		if (this.context.auth?.isTalentSupplierResource()) {
			return <PageNotAllowed redirect="/talent/dashboard" />
		}

		return (
			<div className='talent-portal-container'>
				<div className='talent-portal-page-container px-4'>
					{this.state.isLoading && <LoadingBanner message="Loading Contracts" />}

					{TB.isObject(this.state.error) && (
						<ErrorBanner {...this.state.error}
							onRetry={this.fetchContracts.bind(this)} />
					)}

					{!this.state.isLoading && !TB.isObject(this.state.error) && (
						<>
							<div className='talent-contracts-panel hidden md:flex'>
								<h3 className='mb-5'>{TB.trans('talent.contracts_list.title')}</h3>

								<div className="contracts-container">
									{/*{this.renderMasterContractSection()}*/}

									{this.renderProjectContractsSection()}
								</div>
							</div>

							<div className="md:hidden pt-6">
								<h3 className='mb-5 text-xl text-center'>{TB.trans('talent.contracts_list.title')}</h3>

								{this.renderMobile()}
							</div>
						</>
					)}
				</div>
			</div>
		);
	}
}

const TalentContractsList = (props) => {
	const { auth } = useAuth();

	if (auth.isTalent() && !auth.isProfileApproved()) {
		return <PageNotAllowed redirect="/talent/dashboard" message="Your profile must be approved before accessing this page." />;
	}

	if (auth.isTalentSupplierResource()) {
		return <PageNotAllowed redirect="/talent/dashboard" />;
	}
	return (
		<>
			<ContractsListView />
			{/* <ContractsList navigate={navigate}/> */}
		</>
	)
}

/* ============================================================================
 * New contracts page (Tailwind / functional)
 * Same data + rules as ContractsList: fetchContracts() → opportunities with
 * project.client, document and signed_at. Stacked cards on mobile, table at lg+.
 * ========================================================================== */

const getInitials = (name = '') => String(name)
	.split(/\s+/)
	.map(word => word.replace(/[^a-z0-9]/gi, ''))
	.filter(Boolean)
	.slice(0, 2)
	.map(word => word[0])
	.join('')
	.toUpperCase();

const contractSearchText = opp => [
	opp?.project?.client?.name,
	opp?.project?.name,
	opp?.document?.name,
].filter(Boolean).join(' ').toLowerCase();

const ClientAvatar = ({ client }) => (
	client?.logo_path ? (
		<span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-solid border-gray-200 bg-white p-1">
			<img src={client.logo_path} alt="" className="max-h-full max-w-full object-contain" />
		</span>
	) : (
		<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary" aria-hidden="true">
			{getInitials(client?.name) || <img src={TB.logoImagePath()} alt="" className="h-6 w-6 object-contain" />}
		</span>
	)
);

const ContractRow = ({ opp }) => {
	const client = opp.project?.client;
	const documentName = opp.document?.name || 'N/A';
	const signedAt = opp.signed_at && TB.moment(opp.signed_at).isValid() ? TB.moment(opp.signed_at) : null;

	return (
		<li className="flex flex-col gap-3 px-4 py-4 sm:px-6 lg:grid lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,3fr)_8rem_8rem] lg:items-center lg:gap-6 lg:py-5">
			{/* Client (+ project on mobile) */}
			<div className="flex min-w-0 items-center gap-3">
				<ClientAvatar client={client} />
				<div className="min-w-0">
					<p className="m-0 text-[15px] font-semibold leading-snug text-gray-900">{client?.name}</p>
					<p className="m-0 mt-0.5 text-sm leading-snug text-gray-600 lg:hidden">{opp.project?.name}</p>
				</div>
			</div>

			{/* Project (lg+) */}
			<p className="m-0 hidden text-[15px] leading-snug text-gray-700 lg:block">{opp.project?.name}</p>

			{/* Document */}
			<div className="flex min-w-0 items-center gap-3 rounded-lg bg-gray-50 p-2.5 lg:bg-transparent lg:p-0">
				<span className="flex h-9 w-8 shrink-0 items-end justify-center rounded-md bg-red-50 pb-1 text-[9px] font-bold uppercase text-red-600" aria-hidden="true">
					PDF
				</span>
				<span className="line-clamp-2 min-w-0 break-words text-sm text-gray-700 lg:line-clamp-1" title={documentName}>
					{documentName}
				</span>
			</div>

			{/* Signed date + action: one row on mobile, two grid cells on lg+ */}
			<div className="flex items-center justify-between gap-3 lg:contents">
				<p className="m-0 text-sm text-gray-700 lg:text-[15px]">
					<span className="mr-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 lg:hidden">Signed</span>
					{signedAt ? signedAt.format('MMM D, YYYY') : 'Not yet signed'}
				</p>

				{opp.document?.path ? (
					<a
						href={opp.document.path}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg lg:justify-self-end border border-solid border-primary/30 bg-white px-4 py-2 text-sm font-semibold text-primary hover:border-primary hover:bg-primary/5 hover:text-primary hover:no-underline"
					>
						<EyeOutlined aria-hidden="true" />
						{TB.trans('talent.contracts_list.view_pdf')}
					</a>
				) : (
					<span className="text-sm text-gray-400 lg:justify-self-end">No PDF</span>
				)}
			</div>
		</li>
	);
};

const ContractsListView = () => {
	const { auth } = useAuth();
	const isMounted = useRef(true);

	const [contracts, setContracts] = useState(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState('');

	useEffect(() => () => { isMounted.current = false; }, []);

	const fetchContracts = useCallback(async () => {
		if (!auth) return;

		setContracts(undefined);
		setIsLoading(true);
		setError(null);

		const contractGroups = await auth.fetchContracts().catch(console.warn);
		if (!isMounted.current) return;

		if (!contractGroups) {
			const requestError = await Component.parseRequestError(User.FetchTalentContractsRequest, {
				500: 'error_show_talent_contracts',
			});
			if (!isMounted.current) return;

			setError(requestError);
			setIsLoading(false);
			return;
		}

		setContracts(contractGroups.opportunities || []);
		setIsLoading(false);
	}, [auth]);

	useEffect(() => {
		fetchContracts();
	}, []);

	const filteredContracts = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return contracts || [];
		return (contracts || []).filter(opp => contractSearchText(opp).includes(query));
	}, [contracts, search]);

	const total = contracts?.length || 0;
	const isFiltering = !!search.trim();

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className="flex min-h-[240px] items-center justify-center">
					<LoadingSpinner size="lg" />
				</div>
			);
		}

		if (!total) {
			return (
				<p className="m-0 px-4 py-12 text-center text-sm text-gray-500 sm:px-6">
					{TB.trans('talent.contracts_list.no_projects_msg')}
				</p>
			);
		}

		if (!filteredContracts.length) {
			return (
				<div className="flex flex-col items-center gap-2 px-4 py-12 text-center sm:px-6">
					<p className="m-0 text-sm text-gray-500">No contracts match “{search.trim()}”.</p>
					<button
						type="button"
						onClick={() => setSearch('')}
						className="border-0 bg-transparent p-0 text-sm font-semibold text-primary hover:text-primary-dark"
					>
						Clear search
					</button>
				</div>
			);
		}

		return (
			<>
				<div className="hidden border-0 border-b border-solid border-gray-200 bg-gray-50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 lg:grid lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,3fr)_8rem_8rem] lg:gap-6">
					<span>Client</span>
					<span>Project</span>
					<span>Document</span>
					<span>Signed</span>
					<span aria-hidden="true" />
				</div>

				<ul className="m-0 list-none divide-y divide-gray-200 p-0">
					{filteredContracts.map((opp, index) => (
						<ContractRow key={`talent-project-contract-${opp.id || index}`} opp={opp} />
					))}
				</ul>
			</>
		);
	};

	return (
		<div className="talent-portal-container">
			<div className="mx-auto w-full max-w-[1736px] px-4 py-6 sm:px-6 md:py-10">
				<header className="mb-5 md:mb-7">
					<h1 className="m-0 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">My contracts</h1>
					<p className="m-0 mt-1 text-sm text-gray-500 sm:text-base">
						Signed agreements for your {laravel.platform_name} projects.
					</p>
				</header>

				{TB.isObject(error) ? (
					<div className="rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)]">
						<ErrorBanner {...error} minHeight={280} onRetry={fetchContracts} />
					</div>
				) : (
					<section className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)]">
						<div className="flex flex-col gap-3 border-0 border-b border-solid border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
							<Input
								allowClear
								value={search}
								onChange={e => setSearch(e.target.value)}
								disabled={isLoading || !total}
								prefix={<SearchOutlined className="text-gray-400" />}
								placeholder="Search client, project or document"
								aria-label="Search contracts"
								className="w-full rounded-lg bg-gray-50 py-2 sm:max-w-sm [&_input]:bg-gray-50"
							/>

							{!isLoading && (
								<p className="m-0 shrink-0 text-sm text-gray-500" aria-live="polite">
									{isFiltering && <><span className="font-semibold text-gray-900">{filteredContracts.length}</span> of </>}
									<span className="font-semibold text-gray-900">{total}</span> contract{total === 1 ? '' : 's'}
								</p>
							)}
						</div>

						{renderBody()}
					</section>
				)}
			</div>
		</div>
	);
};

export {
	TalentContractsList
}